import './styles.scss';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { TfiClose } from '@react-icons/all-files/tfi/TfiClose';
import { Typography } from 'Components/atoms';
import { TrainingDueInDaysField } from 'Components/molecules/TrainingDueInDaysField';
import { UserCohort } from 'Components/organisms/UserCohort';
import { ALL_USERS_COHORT, getSelectedUserCohortsFromRows } from 'Components/organisms/UserCohort/Data';
import { CohortFilterDto } from '@ampsec/platform-client';
import { CANCEL } from 'Core-utils/constants/constants';
import { Row } from 'Core-utils/types';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useI18n } from 'Hooks/useI18n';

type LaunchTrainingModalProps = {
    show: boolean;
    moduleName: string;
    onClose: () => void;
    onLaunch: (dueInDays: number, cohorts: CohortFilterDto[]) => void;
    isLaunching?: boolean;
};

const LaunchTrainingModal = ({
    show,
    moduleName,
    onClose,
    onLaunch,
    isLaunching = false,
}: LaunchTrainingModalProps) => {
    const { t } = useI18n();
    const { connectorsByCategoryGroup } = useConnectorData();
    const [dueInDays, setDueInDays] = useState('');
    const [rows, setRows] = useState<Row[]>([]);
    const cohortImageUrl = connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl;

    useEffect(() => {
        if (!show) {
            return;
        }
        setDueInDays('');
        setRows(ALL_USERS_COHORT(cohortImageUrl));
    }, [show, cohortImageUrl]);

    const hasEmptySelectedValue = useMemo(() => rows.some((row) => row.selectedValue === ''), [rows]);

    const handleCancel = () => {
        setDueInDays('');
        onClose();
    };

    const handleLaunch = () => {
        const parsedDueInDays = parseInt(dueInDays, 10);
        if (!dueInDays || Number.isNaN(parsedDueInDays)) {
            return;
        }
        onLaunch(parsedDueInDays, getSelectedUserCohortsFromRows(rows));
    };

    const modalTitle = t('COMPLIANCE_TRAINING_LAUNCH_MODAL_TITLE', 'Launch Training');
    const modalDescription = t(
        'COMPLIANCE_TRAINING_LAUNCH_MODAL_DESCRIPTION',
        'Choose who should receive this training module, then set a due date and launch.',
    );
    const launchButtonLabel = t('COMPLIANCE_TRAINING_LAUNCH_MODAL_SUBMIT', 'Launch Training');
    const isLaunchDisabled = !dueInDays || isLaunching || hasEmptySelectedValue;

    return (
        <Modal show={show} onHide={handleCancel} centered size="lg" className="launch-training-modal">
            <Modal.Header className="launch-training-modal__header bg-body-color text-text-high-emphasis border border-structural-stroke-100 rounded-top">
                <Modal.Title className="body11 p-1">
                    {modalTitle} - {moduleName}
                </Modal.Title>
                <TfiClose
                    className="text-text-high-emphasis btn-close position-absolute end-0 me-2"
                    onClick={handleCancel}
                    size={10}
                />
            </Modal.Header>
            <Modal.Body className="launch-training-modal__body bg-body-color text-text-high-emphasis rounded-bottom border border-structural-stroke-100 border-top-0">
                <Typography variant="body3" color="text-medium-emphasis" className="mb-4">
                    {modalDescription}
                </Typography>
                <div className="launch-training-modal__cohort mb-4">
                    <UserCohort rows={rows} setRows={setRows} variant="dark" mode="locked" />
                </div>
                <TrainingDueInDaysField value={dueInDays} onChange={setDueInDays} disabled={isLaunching} />
                <div className="launch-training-modal__actions d-flex gap-3 justify-content-center mt-4">
                    <Button
                        variant="outline-text-low-emphasis"
                        className="body1 btn-cancel"
                        onClick={handleCancel}
                        disabled={isLaunching}
                    >
                        {CANCEL}
                    </Button>
                    <Button
                        variant="primary-500"
                        className="body1 text-text-black"
                        onClick={handleLaunch}
                        disabled={isLaunchDisabled}
                        data-testid="launch-training-submit"
                    >
                        {isLaunching ? 'Launching...' : launchButtonLabel}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default LaunchTrainingModal;
